import { ReconTransactionDTO } from '../../services/apiClient';
import { TableAction } from '../common/Table';
import { Check, Clock, DollarSign, FilePlus, RotateCcw, Search, UserCog, UserPlus, UserX, X } from 'lucide-react';
import { ComponentConstants } from '../../constants/ComponentConstants';

interface ReconTransactionActionHandlers {
  onViewMember: (idPassportNumber: string) => void;
  onEditMember: (memberId: string) => void;
  onCreateInvoice: (transaction: ReconTransactionDTO) => void;
  onViewInvoiceHistory?: (transaction: ReconTransactionDTO) => void;
  onViewMemberBatches?: (transaction: ReconTransactionDTO) => void;
  onSetIsUnallocated: (transactionId: string, isUnallocated: boolean) => void;
  onSetComponentId?: (transactionId: string, componentId: string) => void;
  onAssignMember?: (transaction: ReconTransactionDTO) => void;
  onIgnoreTransaction?: (transactionId: string) => void;
  showConfirmComponentActions?: boolean;
  showInvoiceHistoryAction?: boolean;
  showMemberBatchesAction?: boolean;
  showAssignMemberAction?: boolean;
  showIgnoreAction?: boolean;
}

const createReconTransactionActions =
  (handlers: ReconTransactionActionHandlers) =>
  (row: ReconTransactionDTO): TableAction[] => {
    const actions: TableAction[] = [];

    const idPassportNumber = row.member?.idPassportNumber?.trim();
    const memberId = (row.memberId ?? '').trim();

    const reconTransactionId = (row.id ?? '').trim();
    const componentId = (row.componentId ?? '').trim();

    if (idPassportNumber) {
      actions.push({
        label: 'View Member',
        onClick: () => handlers.onViewMember(idPassportNumber),
        icon: <Search className="w-4 h-4" />,
      });
    }

    if (memberId) {
      actions.push({
        label: 'Edit Member',
        onClick: () => handlers.onEditMember(memberId),
        icon: <UserCog className="w-4 h-4" />,
      });
    }

    if (memberId) {
      actions.push({
        label: 'Create Invoice',
        onClick: () => handlers.onCreateInvoice(row),
        icon: <FilePlus className="w-4 h-4" />,
      });
    }

    if (memberId && handlers.showInvoiceHistoryAction && handlers.onViewInvoiceHistory) {
      actions.push({
        label: 'Invoice History',
        onClick: () => handlers.onViewInvoiceHistory?.(row),
        icon: <Clock className="w-4 h-4" />,
      });
    }

    if (memberId && handlers.showMemberBatchesAction && handlers.onViewMemberBatches) {
      actions.push({
        label: 'Member Batches',
        onClick: () => handlers.onViewMemberBatches?.(row),
        icon: <DollarSign className="w-4 h-4" />,
      });
    }

    if (reconTransactionId) {

      if (memberId && !componentId && handlers.showConfirmComponentActions && handlers.onSetComponentId) {
        actions.push({
          label: 'Confirm as Membership',
          onClick: () => handlers.onSetComponentId?.(reconTransactionId, ComponentConstants.MEMBERS),
          confirmMessage: 'This will set the component to Members. Continue?',
          icon: <Check className="w-4 h-4" />,
          className: 'text-green-600',
        });

        actions.push({
          label: 'Confirm as RPL',
          onClick: () => handlers.onSetComponentId?.(reconTransactionId, ComponentConstants.RPL),
          confirmMessage: 'This will set the component to RPL. Continue?',
          icon: <Check className="w-4 h-4" />,
          className: 'text-green-600',
        });

        actions.push({
          label: 'Confirm as Competition',
          onClick: () => handlers.onSetComponentId?.(reconTransactionId, ComponentConstants.COMPETITION),
          confirmMessage: 'This will set the component to Competition. Continue?',
          icon: <Check className="w-4 h-4" />,
          className: 'text-green-600',
        });

        actions.push({
          label: 'Confirm as Course',
          onClick: () => handlers.onSetComponentId?.(reconTransactionId, ComponentConstants.DEDICATED_COURSES),
          confirmMessage: 'This will set the component to Dedicated Courses. Continue?',
          icon: <Check className="w-4 h-4" />,
          className: 'text-green-600',
        });
      }

      if (handlers.showAssignMemberAction && handlers.onAssignMember) {
        actions.push({
          label: 'Assign Member',
          onClick: () => handlers.onAssignMember?.(row),
          icon: <UserPlus className="w-4 h-4" />,
        });
      }

      if (row.isUnallocated) {
        actions.push({
          label: 'Reset Allocation',
          onClick: () => handlers.onSetIsUnallocated(reconTransactionId, false),
          confirmMessage: 'This will mark the transaction as allocated again. Continue?',
          icon: <RotateCcw className="w-4 h-4" />,
        });
      } else {
        actions.push({
          label: 'Unallocate Transaction',
          onClick: () => handlers.onSetIsUnallocated(reconTransactionId, true),
          confirmMessage: 'This will move the transaction to Unallocated. Continue?',
          icon: <UserX className="w-4 h-4" />,
          className: 'text-red-600',
        });
      }

      if (handlers.showIgnoreAction && handlers.onIgnoreTransaction) {
        actions.push({
          label: 'Ignore',
          onClick: () => handlers.onIgnoreTransaction?.(reconTransactionId),
          confirmMessage: 'This will ignore this transaction (delete). Continue?',
          icon: <X className="w-4 h-4" />,
          className: 'text-red-600',
        });
      }
    }

    return actions;
  };

export default createReconTransactionActions;
